import { CircleLayer, LineLayer } from '@trimblemaps/trimblemaps-js';
import { FeatureTypes, MapLayers, MapSources } from '../enums';
import { isVisibleUtil } from '../mapUtils';

/**
 * Creates a layer to display circles on the map for each Point and 'featureType' of 'areaCenter' in the given map source.
 * Adds the empty miles areas center layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addEmptyMilesAreasCenterLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: CircleLayer = {
        id: MapLayers.emptyMilesAreasCenterLayer,
        type: 'circle',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            'circle-color': ['get', 'color'],
            'circle-opacity': 1.0,
            'circle-radius': 6,
            'circle-stroke-width': 2,
            'circle-stroke-color': ['get', 'strokeColor'],
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'featureType', FeatureTypes.AreaCenter],
        ],
    };

    if (map.getLayer(MapLayers.emptyMilesAreasCenterLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display circles on the map for each Point and 'featureType' of 'areaLaneCenter' in the given map source.
 * Adds the empty miles areas lane center layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addEmptyMilesAreasLaneCenterLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: CircleLayer = {
        id: MapLayers.emptyMilesAreasLaneCenterLayer,
        type: 'circle',
        source: mapSource,
        layout: {
            visibility,
        },
        paint: {
            'circle-color': ['get', 'color'],
            'circle-opacity': 1.0,
            'circle-radius': 6,
        },
        filter: ['all',
            ['==', '$type', 'Point'],
            ['==', 'featureType', FeatureTypes.AreaLaneCenter],
        ],
    };

    if (map.getLayer(MapLayers.emptyMilesAreasLaneCenterLayer) === undefined) {
        map.addLayer(layer);
    }
};

/**
 * Creates a layer to display lines on the map for each LineString and 'featureType' of 'areaLane' in the given map source.
 * Adds the empty miles areas lane line layer to the map if it doesn't already exist
 * @param map Instance of your map
 * @param mapSource Name of the map source
 * @param isVisible Indicator if the map layer is visible
 */
export const addEmptyMilesAreasLaneLineLayer = ({
    map,
    mapSource,
    isVisible,
}: {
    map: TrimbleMaps.Map;
    mapSource: MapSources;
    isVisible: boolean;
}): void => {
    const visibility = isVisibleUtil(isVisible);

    const layer: LineLayer = {
        id: MapLayers.emptyMilesAreasLaneLineLayer,
        type: 'line',
        source: mapSource,
        layout: {
            'line-join': 'round',
            'line-cap': 'round',
            visibility,
        },
        paint: {
            'line-color': ['get', 'color'],
            'line-width': 6,
            'line-opacity': 0.7,
            'line-offset': 0,
        },
        filter: ['all',
            ['==', '$type', 'LineString'],
            ['==', 'featureType', FeatureTypes.AreaLaneLine],
        ],
    };

    if (map.getLayer(MapLayers.emptyMilesAreasLaneLineLayer) === undefined) {
        map.addLayer(layer);
    }
};
